import {
  FoodItemKey,
  Menu,
  MenuItemBasicWithSides,
  MenuOptionGroupBasicWithItems,
} from "client/jspPlatformExperiment";
import { v4 as uuidv4 } from "uuid";
import { getDisplayMenuOptionGroup } from "utils/menu/menu/menuDataToDisplayUtils";
import { isDisplayMenuItem } from "models/menu/menu";
import {
  deleteMenuOptionByPath,
  getLastUUIDFromPath,
  getMenuOptionByPath,
  modifyMenuOptionByPath,
  removeLastUUIDFromPath,
} from "utils/menu/menu/menuContentDisplay";

export type TypeDisplayMenuOptionGroupOptions = Record<
  string,
  DisplayMenuItem | DisplayMenuSection
>;

export type TypeDisplayMenuSections = Record<string, DisplayMenuSection>;

export interface DisplayMenuItem extends Omit<
  MenuItemBasicWithSides,
  "id" | "side_option_groups" | "food_item"
> {
  id?: number;
  food_item?: FoodItemKey;
  side_option_groups: TypeDisplayMenuSections;
  // `food_tag_overrides` comes from the base type in the API read shape and is
  // mapped to the create shape on save (utils/menu/menu/menuDisplayToDataUtils).
}

export interface DisplayMenuSection extends Omit<
  MenuOptionGroupBasicWithItems,
  "id" | "options"
> {
  id?: number;
  options: TypeDisplayMenuOptionGroupOptions;
}

export const defaultMenuItemDisplay = (): DisplayMenuItem => ({
  food_id: 0,
  display_name: "",
  cap: 0,
  portion_cost: 0,
  portion_price: 0,
  side_option_groups: {},
  option_type: "menu item",
  food_tag_overrides: [],
});

/**
 * The row now points at a different food: drop the stale inline food key and
 * every override, since overrides are validated against the food's own tags
 * and never carry across foods.
 */
export const withFoodChanged = ({
  item,
  foodId,
  originalFoodId,
}: {
  item: DisplayMenuItem;
  foodId: number;
  originalFoodId: number | null;
}): DisplayMenuItem => ({
  ...item,
  food_id: foodId,
  original_food_id: originalFoodId,
  food_item: undefined,
  food_tag_overrides: [],
});

export const defaultMenuSectionDisplay = (): DisplayMenuSection => ({
  name: "",
  options: { [uuidv4()]: defaultMenuItemDisplay() },
  option_type: "option group",
});

const moveMenuOption = (
  menuContents: TypeDisplayMenuSections,
  UUIDPath: string,
  direction: "up" | "down",
): TypeDisplayMenuSections => {
  // Get the parent menu option UUID path
  const parentUUIDPath = removeLastUUIDFromPath(UUIDPath);
  if (parentUUIDPath === undefined) {
    return menuContents;
  }

  // Get the parent menu option and its options
  let menuOptions: TypeDisplayMenuSections | TypeDisplayMenuOptionGroupOptions =
    menuContents;
  let parentMenuOption: DisplayMenuItem | DisplayMenuSection | undefined;

  if (parentUUIDPath !== "") {
    parentMenuOption = getMenuOptionByPath(menuContents, parentUUIDPath);
    if (parentMenuOption === undefined) {
      return menuContents;
    }
    menuOptions = isDisplayMenuItem(parentMenuOption)
      ? parentMenuOption.side_option_groups
      : parentMenuOption.options;
  }

  // Get the moving menu option UUID
  const movingOptionUUID = getLastUUIDFromPath(UUIDPath);
  if (menuOptions[movingOptionUUID] === undefined) {
    return menuContents;
  }

  // Figure out the current index of the moving option
  const keys = Object.keys(menuOptions);
  const currentIdx = keys.indexOf(movingOptionUUID);

  // Check if movement is possible
  if (
    (direction === "up" && currentIdx === 0) ||
    (direction === "down" && currentIdx === keys.length - 1)
  ) {
    return menuContents;
  }

  // Calculate new position
  const newIdx = direction === "up" ? currentIdx - 1 : currentIdx + 1;

  // Manipulate the key ordering
  keys.splice(currentIdx, 1); // Remove the key
  keys.splice(newIdx, 0, movingOptionUUID); // Insert the key at the new position

  // Construct a new section items record with new item order
  const newMenuOptions = keys.reduce((acc, key) => {
    acc[key] = menuOptions[key];
    return acc;
  }, {} as TypeDisplayMenuOptionGroupOptions);

  // Update the menu contents with the new order
  if (parentUUIDPath === "") {
    return newMenuOptions as TypeDisplayMenuSections;
  }

  return modifyMenuOptionByPath(
    menuContents,
    parentUUIDPath,
    isDisplayMenuItem(parentMenuOption)
      ? {
          ...parentMenuOption,
          side_option_groups: newMenuOptions as TypeDisplayMenuSections,
        }
      : {
          ...parentMenuOption,
          options: newMenuOptions,
        },
    "replace",
  );
};

export enum MenuContentActions {
  LOAD_FROM_MENU_DATA = "LOAD_FROM_MENU_DATA",
  ADD_OPTION_GROUP = "ADD_OPTION_GROUP",
  ADD_ITEM = "ADD_ITEM",
  UPDATE_OPTION = "UPDATE_OPTION",
  REMOVE_OPTION = "REMOVE_OPTION",
  MOVE_OPTION_UP = "MOVE_OPTION_UP",
  MOVE_OPTION_DOWN = "MOVE_OPTION_DOWN",
}

export interface MenuContentAction {
  type: MenuContentActions;
  UUIDPath: string;
  menuData?: Menu;
  newOptionGroupFields?: Partial<DisplayMenuSection>;
  newItemFields?: Partial<DisplayMenuItem>;
}

export const menuContentsReducer = (
  menuContents: TypeDisplayMenuSections,
  action: MenuContentAction,
): TypeDisplayMenuSections => {
  switch (action.type) {
    case MenuContentActions.LOAD_FROM_MENU_DATA: {
      return Object.fromEntries(
        action.menuData!.sections.map((section) => [
          uuidv4(),
          getDisplayMenuOptionGroup(section),
        ]),
      );
    }

    case MenuContentActions.ADD_OPTION_GROUP: {
      const effectParentMenuOption = getMenuOptionByPath(
        menuContents,
        action.UUIDPath,
      );
      if (effectParentMenuOption !== undefined) {
        if (isDisplayMenuItem(effectParentMenuOption)) {
          return modifyMenuOptionByPath(menuContents, action.UUIDPath, {
            ...effectParentMenuOption,
            side_option_groups: {
              ...effectParentMenuOption.side_option_groups,
              [uuidv4()]: defaultMenuSectionDisplay(),
            },
          });
        }
        return modifyMenuOptionByPath(menuContents, action.UUIDPath, {
          ...effectParentMenuOption,
          options: {
            ...effectParentMenuOption.options,
            [uuidv4()]: defaultMenuSectionDisplay(),
          },
        });
      }
      return {
        ...menuContents,
        [uuidv4()]: defaultMenuSectionDisplay(),
      };
    }

    case MenuContentActions.ADD_ITEM: {
      const effectParentMenuOption = getMenuOptionByPath(
        menuContents,
        action.UUIDPath,
      );
      if (
        effectParentMenuOption !== undefined &&
        !isDisplayMenuItem(effectParentMenuOption)
      ) {
        return modifyMenuOptionByPath(menuContents, action.UUIDPath, {
          ...effectParentMenuOption,
          options: {
            ...effectParentMenuOption.options,
            [uuidv4()]: {
              ...defaultMenuItemDisplay(),
              ...(action.newItemFields ?? {}),
            },
          },
        });
      }
      return menuContents;
    }

    case MenuContentActions.UPDATE_OPTION: {
      if (action.newItemFields || action.newOptionGroupFields) {
        return modifyMenuOptionByPath(
          menuContents,
          action.UUIDPath,
          (action.newItemFields ?? action.newOptionGroupFields)!,
          "update",
        );
      }
      return menuContents;
    }
    case MenuContentActions.REMOVE_OPTION: {
      return deleteMenuOptionByPath(menuContents, action.UUIDPath);
    }

    case MenuContentActions.MOVE_OPTION_UP:
      return moveMenuOption(menuContents, action.UUIDPath, "up");

    case MenuContentActions.MOVE_OPTION_DOWN:
      return moveMenuOption(menuContents, action.UUIDPath, "down");

    default: {
      return menuContents;
    }
  }
};
